<main>
  <loading-overlay [isLoading]="isLoading() > 0" [fullWidth]="true" />
  <p-toast position="bottom-right" key="br" />

  <header>
    <div class="headline">
      <h1>{{ headerTitle() }}</h1>
      @if (fund(); as current) {
        <p-tag [severity]="statusSeverity(current.status)" [value]="statusLabel(current.status)" />
      } @else {
        <p-tag severity="warn" [value]="'PettyCashFund_Status_Draft' | translateWF: 'Draft'" />
      }
    </div>
    <div class="buttons">
      @if (canDelete()) {
        <p-button [label]="'Delete' | translateWF: 'Delete'" icon="pi pi-trash" severity="danger" variant="outlined" (onClick)="confirmDelete()" />
      }
      @if (canMarkReady()) {
        <p-button
          [label]="'PettyCashFund_ReadyForFunding_Action' | translateWF: 'Mark ready for funding'"
          icon="pi pi-send"
          variant="outlined"
          (onClick)="confirmReadyForFunding()"
        />
      }
      @if (isActive()) {
        <p-button [label]="'Receipt_Title' | translateWF: 'Capture receipts'" icon="pi pi-receipt" variant="outlined" (onClick)="captureReceipts()" />
      }
      @if (isActive() && isCustodian()) {
        <p-button [label]="'ExpenseClaim_New' | translateWF: 'New expense claim'" icon="pi pi-file-check" variant="outlined" (onClick)="newClaim()" />
      }
      @if (isActive() && canFund) {
        <p-button [label]="'ExpenseSettlement_Replenish' | translateWF: 'Replenish'" icon="pi pi-calculator" variant="outlined" (onClick)="replenish()" />
      }
      @if ((canDelete() || canMarkReady() || isActive()) && canEdit && !isClosed()) {
        <span class="separator"></span>
      }
      @if (canEdit && !isClosed()) {
        <p-splitbutton [label]="'Save' | translateWF: 'Save'" (onClick)="save(false)" [model]="saveMenuItems" />
      }
      <p-button variant="outlined" (onClick)="back()">{{ 'Back' | translateWF: 'Back' }}</p-button>
    </div>
  </header>

  <div class="content">
    <p-confirmDialog />

    @if (fund(); as current) {
      <div class="cs-summary-grid">
        <p-card data-variant="summary" [header]="'PettyCashFund_Col_Authorized' | translateWF: 'Authorized amount'">
          <ng-template pTemplate="subtitle">{{ symbolOf(current.currency) }} {{ current.authorizedAmount | number: '1.2-2' }}</ng-template>
          <span class="flex items-center gap-2">
            <i class="pi pi-user" aria-hidden="true"></i>
            {{ current.custodian.displayName }} · {{ current.number }}
          </span>
        </p-card>
        <p-card data-variant="summary" [header]="'PettyCashFund_Kpi_CurrentBalance' | translateWF: 'Current balance'">
          <ng-template pTemplate="subtitle"> {{ symbolOf(current.currency) }} {{ balance()?.currentBalance ?? 0 | number: '1.2-2' }} </ng-template>
          <div class="flex flex-col gap-2">
            @if (balanceBar(); as bar) {
              <div class="relative">
                <p-progressbar [value]="bar.value" [showValue]="false" [color]="bar.color" styleClass="h-2" />
                <span
                  class="absolute -top-1 h-4 w-0.5 bg-surface-500"
                  [style.left.%]="bar.minAt"
                  [attr.aria-label]="'PettyCashFund_Field_Min' | translateWF: 'Minimum revolving balance'"
                ></span>
              </div>
            }
            <div class="flex justify-between gap-2 text-sm">
              <span>{{ 'PettyCashFund_Kpi_Min' | translateWF: 'Min' }} {{ current.minRevolvingBalance | number: '1.2-2' }}</span>
              <span>{{ 'PettyCashFund_Kpi_Max' | translateWF: 'Max' }} {{ current.maxRevolvingBalance | number: '1.2-2' }}</span>
            </div>
            @if (!isActive() && !isClosed()) {
              <small class="text-surface-500">{{ 'PettyCashFund_Kpi_Balance_NotOpened' | translateWF: 'Starts with the treasurer’s opening movement.' }}</small>
            } @else if (balanceBar()?.level === 'empty') {
              <small class="text-red-500">{{ 'PettyCashFund_Kpi_Balance_Empty' | translateWF: 'No cash left — post an expense claim to replenish it.' }}</small>
            } @else if (balanceBar()?.level === 'low') {
              <small class="text-orange-500">{{ 'PettyCashFund_Kpi_Balance_Low' | translateWF: 'Below the minimum — prepare an expense claim.' }}</small>
            } @else if (balance()) {
              <small class="text-surface-500">
                {{ balance()?.postedClaims }} {{ 'PettyCashFund_Kpi_Balance_PostedClaims' | translateWF: 'posted claims' }} · {{ symbolOf(current.currency) }}
                {{ balance()?.postedClaimsTotal | number: '1.2-2' }}
              </small>
            }
          </div>
        </p-card>
      </div>
    }

    @if (ready()) {
      <div class="grid grid-cols-12 gap-5">
        <div class="col-span-12 xl:col-span-8 flex flex-col gap-5">
          <p-panel [header]="'PettyCashFund_Section_Data' | translateWF: 'Fund data'">
            <p-card>
              <form [formGroup]="form" class="grid grid-cols-12 gap-5">
                <div class="form-box col-span-12 md:col-span-8">
                  <label for="fund-name">{{ fieldLabels.name | translateWF: 'Name' }} *</label>
                  <input id="fund-name" pInputText class="w-full" formControlName="name" maxlength="120" />
                </div>
                <div class="form-box col-span-12 md:col-span-4">
                  <label>{{ 'PettyCashFund_Col_Number' | translateWF: 'Number' }}</label>
                  <span class="font-mono py-2">{{ fund()?.number ?? ('PettyCashFund_Number_Pending' | translateWF: 'Assigned on save') }}</span>
                </div>

                <div class="form-box col-span-12 md:col-span-8">
                  <label for="fund-custodian">{{ fieldLabels.custodianUsername | translateWF: 'Custodian' }} *</label>
                  <p-select
                    inputId="fund-custodian"
                    formControlName="custodianUsername"
                    [options]="users()"
                    optionLabel="displayName"
                    optionValue="username"
                    [filter]="true"
                    filterBy="displayName,username"
                    [placeholder]="'PettyCashFund_Select_User' | translateWF: 'Select a user'"
                    styleClass="w-full"
                  />
                  <small class="text-surface-500">
                    {{ 'PettyCashFund_Custodian_Hint' | translateWF: 'Holds the cash and captures the receipts the fund pays.' }}
                  </small>
                </div>
                <div class="form-box col-span-12 md:col-span-4">
                  <label for="fund-currency">{{ fieldLabels.currency | translateWF: 'Currency' }} *</label>
                  <p-select inputId="fund-currency" formControlName="currency" [options]="currencyOptions" optionLabel="label" optionValue="value" styleClass="w-full" />
                </div>

                <div class="form-box col-span-12">
                  <label for="fund-members">{{ fieldLabels.memberUsernames | translateWF: 'Members' }}</label>
                  <p-multiselect
                    inputId="fund-members"
                    formControlName="memberUsernames"
                    [options]="users()"
                    optionLabel="displayName"
                    optionValue="username"
                    [filter]="true"
                    filterBy="displayName,username"
                    display="chip"
                    [placeholder]="'PettyCashFund_Select_Members' | translateWF: 'Select members'"
                    styleClass="w-full"
                  />
                  <small class="text-surface-500">
                    {{ 'PettyCashFund_Members_Hint' | translateWF: 'May capture receipts and build claims for this fund.' }}
                  </small>
                </div>

                <div class="form-box col-span-12 md:col-span-6">
                  <label for="fund-min">{{ fieldLabels.minRevolvingBalance | translateWF: 'Minimum revolving balance' }} *</label>
                  <p-inputGroup>
                    <p-inputnumber
                      inputId="fund-min"
                      formControlName="minRevolvingBalance"
                      mode="decimal"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      [min]="0"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                    />
                    <p-inputGroupAddon>{{ currencyAddon() }}</p-inputGroupAddon>
                  </p-inputGroup>
                  <small class="text-surface-500">
                    {{ 'PettyCashFund_Min_Hint' | translateWF: 'Below this the custodian claims and asks for replenishment.' }}
                  </small>
                </div>
                <div class="form-box col-span-12 md:col-span-6">
                  <label for="fund-max">{{ fieldLabels.maxRevolvingBalance | translateWF: 'Maximum revolving balance' }} *</label>
                  <p-inputGroup>
                    <p-inputnumber
                      inputId="fund-max"
                      formControlName="maxRevolvingBalance"
                      mode="decimal"
                      [minFractionDigits]="2"
                      [maxFractionDigits]="2"
                      [min]="0"
                      styleClass="w-full"
                      inputStyleClass="w-full"
                    />
                    <p-inputGroupAddon>{{ currencyAddon() }}</p-inputGroupAddon>
                  </p-inputGroup>
                  <small class="text-surface-500">{{ 'PettyCashFund_Max_Hint' | translateWF: 'The most cash the fund may hold.' }}</small>
                </div>

                <div class="form-box col-span-12">
                  <app-financial-account-select
                    [label]="fieldLabels.glAccountReference | translateWF: 'Fund account'"
                    [referenceControl]="form.controls.glAccountReference"
                    [numberControl]="glNumberControl"
                    [nameControl]="glNameControl"
                    [postingModule]="pettyCash"
                    [subledgerType]="fundSubledger"
                    [postingOnly]="true"
                    [required]="true"
                    [readonly]="!canEdit || !isDraft()"
                  />
                  <small class="text-surface-500">
                    {{
                      'PettyCashFund_GlAccount_Hint'
                        | translateWF: 'Petty cash fund accounts (subledger "Petty cash fund"). Accounting adds them under Caja chica; several funds may share one.'
                    }}
                  </small>
                </div>

                <div class="form-box col-span-12">
                  <label for="fund-description">{{ fieldLabels.description | translateWF: 'Description' }}</label>
                  <textarea id="fund-description" pTextarea class="w-full" rows="3" formControlName="description" maxlength="500"></textarea>
                </div>
              </form>
            </p-card>
          </p-panel>

          @if (showOpening() && fund(); as current) {
            <app-fund-opening [fund]="current" [canFund]="canFund" (funded)="onFunded()" />
          }
        </div>

        <div class="col-span-12 xl:col-span-4 flex flex-col gap-5">
          <p-panel [header]="'PettyCashFund_Field_Policy' | translateWF: 'Approval policy'">
            <p-card>
              <div class="flex flex-col gap-4">
                <div class="form-box">
                  <label for="fund-policy">{{ 'PettyCashFund_Policy_Label' | translateWF: 'Policy for this fund’s claims' }}</label>
                  <p-select
                    inputId="fund-policy"
                    [formControl]="form.controls.claimPolicyReference"
                    [options]="policies()"
                    optionValue="id"
                    [showClear]="true"
                    [placeholder]="'PettyCashFund_Policy_Default' | translateWF: 'Default claim policy'"
                    styleClass="w-full"
                  >
                    <ng-template #selectedItem let-policy>{{ policyName(policy) }}</ng-template>
                    <ng-template #item let-policy>{{ policyName(policy) }}</ng-template>
                  </p-select>
                  <small class="text-surface-500">
                    {{ 'PettyCashFund_Policy_Hint' | translateWF: 'Approves the receipts of this fund’s expense claims. Empty uses the default expense claim policy.' }}
                  </small>
                </div>

                @if (selectedPolicy(); as policy) {
                  <div class="flex flex-col gap-2">
                    <span class="font-semibold">{{ 'PettyCashFund_Policy_Approvers' | translateWF: 'Approvers' }}</span>
                    @for (approver of policy.approvers; track $index) {
                      <div class="flex items-center justify-between gap-3 py-1">
                        <span class="flex items-center gap-2">
                          <i [class]="approver.type === 1 ? 'pi pi-users' : 'pi pi-user'" aria-hidden="true"></i>
                          {{ approver.value }}
                        </span>
                        <span class="text-sm text-surface-500">
                          @if (approver.order) {
                            {{ 'PettyCashFund_Policy_Order' | translateWF: 'Order' }} {{ approver.order }} ·
                          }
                          @if (approver.fromAmountUsd) {
                            {{ 'PettyCashFund_Policy_From' | translateWF: 'from USD' }} {{ approver.fromAmountUsd | number: '1.0-2' }} ·
                          }
                          {{ approver.isRequired ? ('PettyCashFund_Policy_Required' | translateWF: 'Required') : ('PettyCashFund_Policy_Optional' | translateWF: 'Optional') }}
                        </span>
                      </div>
                    }
                  </div>
                }
              </div>
            </p-card>
          </p-panel>

          @if (fund(); as current) {
            <p-panel [header]="'PettyCashFund_Section_Movements' | translateWF: 'Fund movements'">
              <p-card>
                <small class="text-surface-500">
                  @if (isDraft()) {
                    {{
                      'PettyCashFund_Movements_Draft' | translateWF: 'No movements yet. Mark the fund ready for funding; the treasurer then posts its opening and it becomes Active.'
                    }}
                  } @else if (isReadyForFunding()) {
                    {{ 'PettyCashFund_Movements_Ready' | translateWF: 'Waiting for the treasurer to post the opening movement.' }}
                  } @else {
                    {{ 'PettyCashFund_Movements_Hint' | translateWF: 'The fund was opened from the bank; from now on expense claims replenish it.' }}
                  }
                </small>
              </p-card>
            </p-panel>
          }

          @if (isActive()) {
            <p-panel [header]="'ExpenseClaim_Title' | translateWF: 'Expense claims'">
              <app-owner-claims [ownerReference]="currentFundId()" />
            </p-panel>
            <p-panel [header]="'ExpenseSettlement_Replenishments' | translateWF: 'Replenishments'">
              <app-owner-settlements [ownerReference]="currentFundId()" />
            </p-panel>
          }
        </div>
      </div>
    }
  </div>
</main>
